import { observer } from 'mobx-react-lite';

import { Icons } from '@/assets/icons/icons';
import { XStack } from '@/components/ui';
import { InitialsAvatar } from '@/components/ui/Avatar';
import { Button } from '@/components/ui/Button';
import { userStore } from '@/stores/user.store';

import { useProjectMembers } from '../project-members.query';

import {
  getOtherMembers,
  getProjectTeamName,
  isProjectPrivate,
} from './project-visibility';

import type { Project } from '@littlebird/sdk';

type ProjectShareButtonProps = {
  project: Project;
  onOpen: () => void;
};

export const ProjectShareButton = observer(
  ({ project, onOpen }: ProjectShareButtonProps) => {
    const { data: members, isLoading } = useProjectMembers(project.id, true);
    const others = getOtherMembers(members, userStore.userId);
    const isShared = !isLoading && !isProjectPrivate(project, others);

    return (
      <Button
        size="sm"
        variant={isShared ? 'secondary' : 'default'}
        aria-label="Share project"
        onClick={onOpen}
      >
        {isShared ? (
          <XStack className="-space-x-2">
            {others.slice(0, 3).map((member) => {
              const name = member.teamId
                ? getProjectTeamName(member)
                : (member.displayName ?? member.email ?? 'Unknown');
              return (
                <InitialsAvatar
                  key={
                    member.membershipId ?? `${member.userId}:${member.teamId}`
                  }
                  name={name}
                  seed={member.teamId ?? member.email ?? name}
                  className="size-6 border-2 border-background"
                />
              );
            })}
            {others.length > 3 && (
              <span className="flex size-6 items-center justify-center rounded-full border-2 border-background bg-surface-active text-[0.625rem] font-semibold">
                +{others.length - 3}
              </span>
            )}
          </XStack>
        ) : (
          <Icons.share className="size-3.5" />
        )}
        Share
      </Button>
    );
  }
);
